import { ConversationVoiceService } from './conversation-voice.service';
import { sseFrames } from './sse-frames';
import { ChatExecutionOptions, ReasoningEffort, SessionSettings } from './chat-execution';
import type { Point } from 'geojson';
import { focusPinIcon, PIN_HEIGHT } from '../map/core/place-pin';
import { ZipCodeAreaFeature } from '../map/features/zipcode-areas/zipcode-area-feature';
import { CacheService } from './cache.service';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { ApiKeysService } from './api-keys.service';
import { AgentProblem, parseAgentProblem } from './agent-problem';
import { Subject, Subscription, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { AppContextService } from './app-context.service';
import { MapObjectReference } from '../map/core/map-object';
import { MapNavigationService } from '../map/core/map-navigation.service';
import {
  CurrentSurfaceActionService,
  LeadEmailDraftAction,
  LeadTableViewAction,
  SurfaceActionReceipt,
  parseLeadEmailDraftAction,
  parseLeadTableViewAction,
} from './current-surface-action.service';

/** Agent connection stored by the conversation. 'chat' remains the persisted
 *  compatibility value for API access; 'codex' uses the per-user CLI profile. */
export type ProviderMode = 'chat' | 'codex';

/** One configured chat key as the picker lists it (display only, no secret).
 *  `isPrimary` is the company default. */
export interface ChatKeyOption { id: number; provider: string; model: string | null; isPrimary: boolean; }

/** A geocoder-resolved location that the user can choose to reveal on the map. */
export interface MapLocateAction {
  type: 'map-locate';
  label: string;
  latitude: number;
  longitude: number;
  zoom?: number;
  object?: MapObjectReference;
}

export interface LeadTableViewMessageAction {
  action: LeadTableViewAction;
  status: 'proposed' | 'applied' | 'restored';
  receipt?: SurfaceActionReceipt;
  feedback?: string;
}

export interface LeadEmailDraftMessageAction {
  action: LeadEmailDraftAction;
  status: 'proposed' | 'applied';
  feedback?: string;
}

/** One conversation turn shown in the transcript. Agent turns can carry a live
 *  "process" trace (the steps an agent streams while it works). */
export interface ChatMessage {
  id?: string;
  voice?: boolean;
  voiceSourceIds?: string[];
  voicePending?: { revision: number };
  turnId?: string;
  sequence?: number;
  status?: 'running' | 'completed' | 'failed' | 'interrupted';
  role: 'user' | 'agent';
  text: string;
  senderName?: string; // Product-facing identity, independent of the engine used for this reply.
  /** Execution record returned by the backend; message parts contain content only. */
  turn?: { id: string; sessionId: string; provider: string; apiKeyId: number | null; model: string | null; effort: ReasoningEffort | null; status: string };
  steps?: string[];   // agent-only: the live process trace
  working?: boolean;  // agent-only: still streaming
  problem?: AgentProblem;
  diagnosticId?: string;
  error?: boolean;    // this bubble is a surfaced error — kept out of replayed history
  mapLocate?: MapLocateAction; // agent-only: trusted tool result, never parsed from prose
  leadTableView?: LeadTableViewMessageAction; // agent-only: trusted typed SSE frame
  leadEmailDraft?: LeadEmailDraftMessageAction; // agent-only: local composer proposal
  overviewAnalyses?: OverviewAnalysisPublished[]; // agent-only: trusted publish tool results, saved in parts
}

/** A saved Overview report published by the validated backend tool in this turn. */
export interface OverviewAnalysisPublished { id: string; reportId: string }

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Accepts only the typed publish shape from live frames and saved message parts. */
function parseOverviewAnalysisPublished(value: unknown): OverviewAnalysisPublished | null {
  const item = value as { id?: unknown; reportId?: unknown } | null;
  return item && typeof item.id === 'string' && uuidPattern.test(item.id)
    && typeof item.reportId === 'string' && uuidPattern.test(item.reportId)
    ? { id: item.id, reportId: item.reportId } : null;
}

/** Cached persistent conversation state. Routes and map
 *  state belong to their existing owners; switching a conversation never navigates. */
export interface CommandConversation {
  id: string;
  title: string;
  draft: string;
  messages: ChatMessage[];
  provider: ProviderMode;
  selectedChatKeyId: number | null;
  sessions?: SessionSettings[];
  isArchived?: boolean;
  archiving?: boolean;
  pendingArchive?: boolean;
  archiveError?: string;
  persisted?: boolean;
  revision?: number;
  loaded?: boolean;
  beforeSequence?: number | null;
  saving?: boolean;
  clearing?: boolean;
  loadGeneration?: number;
  savedSettings?: string;
  saveError?: string;
  voiceSaveError?: string;
  loadError?: string;
  sendError?: string;
  unconfirmedTurn?: { turnId: string; prompt: string; restoredDraft?: boolean };
  panelOpen: boolean;
}

interface ConversationTurn {
  turnId: string;
  conversation: CommandConversation;
  prompt: string;
  abort: AbortController;
  token: string | null;
  completion?: Promise<ChatMessage>;
}

interface ConversationHistory {
  loading: boolean;
  loaded: boolean;
  error: string;
  nextOffset: number | null;
  generation: number;
}

/** Conversation state and UI actions. The backend owns history, sender identity,
 * Agent sessions and streaming checkpoints; the browser owns transient map receipts. */
@Injectable({ providedIn: 'root' })
export class CommandConversationService implements OnDestroy {
  private readonly keyChanges: Subscription;
  private readonly history: ConversationHistory = { loading: false, loaded: false, error: '', nextOffset: null, generation: 0 };
  private readonly archiveHistory: ConversationHistory = { loading: false, loaded: false, error: '', nextOffset: null, generation: 0 };
  get loadingHistory(): boolean { return this.history.loading; }
  get nextHistoryOffset(): number | null { return this.history.nextOffset; }
  get archivedHistory(): Readonly<ConversationHistory> { this.ensureAccount(); return this.archiveHistory; }
  loadingMessages = false;
  private retrying = false;
  get persistenceError(): string {
    this.ensureAccount();
    if (this.history.error || this.archiveHistory.error) return this.history.error || this.archiveHistory.error;
    const conversation = this.conversationError(this.active) ? this.active
      : this.items.find(c => this.conversationError(c));
    return conversation ? conversation.title + ': ' + this.conversationError(conversation) : '';
  }

  private conversationError(c: CommandConversation): string {
    return c.archiveError || c.saveError || c.voiceSaveError || c.loadError || c.sendError || '';
  }
  private initialHistorySelection = true;
  private selectionRevision = 0;
  private readonly saves = new Map<string, Promise<void>>();
  private readonly saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly messageLoads = new Map<string, Promise<void>>();
  private readonly polls = new Map<string, ReturnType<typeof setTimeout>>();
  private items: CommandConversation[] = [this.emptyConversation()];
  private active = this.items[0];
  private visibleItems = this.items;
  private visibleMessages: ChatMessage[] = [];
  private archivedItems: CommandConversation[] = [];
  private accountToken: string | null | undefined;
  private voiceStarting = false;
  private readonly turns = new Map<string, ConversationTurn>();
  private readonly selectionSource = new Subject<void>();
  readonly conversationSelected$ = this.selectionSource.asObservable();
  private readonly overviewAnalysisSource = new Subject<OverviewAnalysisPublished>();
  /** Typed publish results, so report lists update without parsing prose or polling. */
  readonly overviewAnalysisPublished$ = this.overviewAnalysisSource.asObservable();

  /** Shared company configuration; each conversation owns its engine choice. */
  get chatKeys(): ChatKeyOption[] { return this.cache.get<{ keys: ChatKeyOption[] }>('chat', 'config')?.keys ?? []; }

  get conversations(): readonly CommandConversation[] { this.ensureAccount(); return this.visibleItems; }

  get archivedConversations(): readonly CommandConversation[] { this.ensureAccount(); return this.archivedItems; }

  private refreshVisibleConversations(): void {
    this.visibleItems = this.items.filter(c => !c.isArchived);
    this.archivedItems = this.items.filter(c => c.isArchived);
  }
  get activeConversation(): CommandConversation { this.ensureAccount(); return this.active; }
  get provider(): ProviderMode { return this.activeConversation.provider; }
  set provider(value: ProviderMode) { if (value !== this.provider) void this.voice.stop(); this.activeConversation.provider = value; this.scheduleSave(this.active); }
  get sessions(): readonly SessionSettings[] { return this.activeConversation.sessions ?? []; }
  get model(): string | null { return this.sessions.find(s => s.agentKey === 'bree')?.model ?? null; }
  set model(value: string | null) { this.updateAgentSettings({ agentKey: 'bree', model: value, effort: this.effort }); }
  get effort(): ReasoningEffort | null { return this.sessions.find(s => s.agentKey === 'bree')?.effort ?? null; }
  set effort(value: ReasoningEffort | null) { this.updateAgentSettings({ agentKey: 'bree', model: this.model, effort: value }); }

  /** Typed and spoken turns resolve the same way: BRee's saved settings, then the conversation's API key model,
   *  then the composer's choice. Anything still unresolved is left to the backend's defaults. */
  private execution(conversation: CommandConversation, fallback: ChatExecutionOptions = {}): ChatExecutionOptions {
    const bree = conversation.sessions?.find(s => s.agentKey === 'bree');
    const saved = { model: bree?.model ?? null, effort: bree?.effort ?? null };
    const keyModel = conversation.provider === 'chat'
      ? this.chatKeys.find(k => k.id === this.chatKeyId(conversation))?.model?.trim() || undefined : undefined;
    return {
      model: saved.model ?? keyModel ?? fallback.model,
      effort: saved.effort ?? fallback.effort,
    };
  }

  updateAgentSettings(value: SessionSettings): void {
    const conversation = this.activeConversation;
    conversation.sessions = [...(conversation.sessions ?? []).filter(s => s.agentKey !== value.agentKey), { ...value }]
      .sort((a, b) => a.agentKey.localeCompare(b.agentKey));
    this.scheduleSave(conversation);
  }
  get panelOpen(): boolean { return this.activeConversation.panelOpen; }
  set panelOpen(value: boolean) { this.activeConversation.panelOpen = value; }
  get messages(): ChatMessage[] {
    const messages = this.activeConversation.messages;
    const spoken = new Set(messages.filter(m => m.voice && m.role === 'user').map(m => m.id));
    const hidden = (m: ChatMessage) => !!m.voiceSourceIds?.length && m.voiceSourceIds.every(id => spoken.has(id));
    if (!spoken.size || !messages.some(hidden)) return messages;
    const visible = messages.filter(m => !hidden(m));
    // Keep one stable derived array for change detection while visible identities are unchanged.
    if (visible.length !== this.visibleMessages.length || visible.some((m, i) => m !== this.visibleMessages[i]))
      this.visibleMessages = visible;
    return this.visibleMessages;
  }
  set messages(value: ChatMessage[]) { this.activeConversation.messages = value; }
  get draft(): string { return this.activeConversation.draft; }
  set draft(value: string) { this.activeConversation.draft = value; this.scheduleSave(this.active); }
  get selectedChatKeyId(): number | null { return this.activeConversation.selectedChatKeyId; }
  set selectedChatKeyId(value: number | null) { this.activeConversation.selectedChatKeyId = value; this.scheduleSave(this.active); }

  newConversation(): void {
    void this.voice.stop();
    this.ensureAccount();
    this.selectionRevision++;
    // Reuse an untouched new conversation rather than filling history with blanks.
    this.active = this.items.find(c => !c.isArchived && c.title === 'New conversation'
      && !c.persisted && !c.messages.length && !c.draft.trim()) ?? this.emptyConversation();
    if (!this.items.includes(this.active)) this.items.unshift(this.active);
    this.refreshVisibleConversations();
    this.selectionSource.next();
  }

  selectConversation(id: string, options = { openPanel: true }): void {
    this.ensureAccount();
    const conversation = this.items.find(c => c.id === id);
    if (!conversation) return;
    this.selectionRevision++;
    // A call belongs to its conversation; only the Composer Live voice button starts another.
    void this.voice.stop();
    this.active = conversation;
    conversation.panelOpen = options.openPanel;
    if (conversation.persisted) void this.loadMessages(conversation);
    this.selectionSource.next();
  }

  renameConversation(id: string, title: string): void {
    const conversation = this.conversations.find(c => c.id === id);
    if (conversation && title.trim()) {
      conversation.title = title.trim().slice(0, 100);
      this.scheduleSave(conversation);
    }
  }

  isWorking(conversation: CommandConversation): boolean {
    return !!conversation.archiving || !!conversation.saving || !!conversation.clearing || conversation.messages.some(message => message.working);
  }

  private resetHistory(history = this.history): void {
    history.generation++;
    history.loaded = false;
    history.loading = false;
    history.nextOffset = null;
  }

  async setArchived(id: string, archived: boolean): Promise<void> {
    this.ensureAccount();
    const conversation = this.items.find(c => c.id === id);
    const token = this.accountToken;
    if (!conversation || conversation.archiving || conversation.saving || conversation.clearing) return;
    conversation.archiving = true;
    if (this.active === conversation) await this.voice.stop();
    conversation.pendingArchive = archived;
    const timer = this.saveTimers.get(id);
    if (timer) clearTimeout(timer);
    this.saveTimers.delete(id);
    try {
      // Flush draft/rename changes before the separate archive revision write.
      await this.saveSettings(conversation);
      if (token !== this.auth.getToken()) return;
      const updated = await firstValueFrom(this.http.put<ConversationMetadata>('/api/conversations/' + id + '/archive',
        { revision: conversation.revision, isArchived: archived }));
      if (token !== this.auth.getToken()) return;
      conversation.isArchived = updated.isArchived;
      conversation.revision = updated.revision;
      conversation.pendingArchive = undefined;
      conversation.archiveError = undefined;
      this.refreshVisibleConversations();
      const reloadArchive = this.archiveHistory.loaded || this.archiveHistory.loading || !!this.archiveHistory.error;
      this.resetHistory();
      this.resetHistory(this.archiveHistory);
      if (reloadArchive) void this.loadHistory(false, true);
      if (archived && this.active === conversation) {
        const next = this.items.find(c => c !== conversation && !c.isArchived);
        if (next) this.selectConversation(next.id);
        else this.newConversation();
      }
      void this.loadHistory();
    } catch {
      if (token === this.auth.getToken()) conversation.archiveError = archived
        ? 'Could not archive this conversation. Please retry.'
        : 'Could not restore this conversation. Please retry.';
    } finally {
      conversation.archiving = false;
      if (token === this.auth.getToken() && conversation.savedSettings !== this.settings(conversation)) this.scheduleSave(conversation);
    }
  }

  private emptyConversation(): CommandConversation {
    return {
      id: crypto.randomUUID(), title: 'New conversation', draft: '', messages: [],
      provider: 'codex', selectedChatKeyId: null, panelOpen: false, loaded: true,
    };
  }

  /** Never expose one account's transcript after logout or a different login. */
  private ensureAccount(): void {
    const token = this.auth.getToken();
    if (token === this.accountToken) return;
    if (this.accountToken !== undefined) {
      void this.voice.stop();
      for (const turn of this.turns.values()) turn.abort.abort();
      this.turns.clear();
      this.items = [this.emptyConversation()];
      this.active = this.items[0];
      this.visibleMessages = [];
      this.cache.clear('chat');
      for (const timer of [...this.saveTimers.values(), ...this.polls.values()]) clearTimeout(timer);
      this.saveTimers.clear(); this.polls.clear(); this.saves.clear(); this.messageLoads.clear();
      this.resetHistory(); this.resetHistory(this.archiveHistory);
      this.history.error = ''; this.archiveHistory.error = ''; this.loadingMessages = false;
      this.initialHistorySelection = true;
      this.refreshVisibleConversations();
    }
    this.accountToken = token;
    if (token) queueMicrotask(() => { if (token === this.auth.getToken()) void this.loadHistory(); });
  }

  async locateOnMap(location: MapLocateAction): Promise<void> {
    if (location.object?.featureKey === this.zipCodeAreas.key) {
      await this.zipCodeAreas.navigateToZipCode(String(location.object.objectId));
    } else if (location.object?.featureKey === 'leads' || location.object?.featureKey === 'customers') {
      const { featureKey, objectId } = location.object;
      const id = encodeURIComponent(String(objectId));
      const objectUrl = featureKey === 'leads'
        ? `/api/map-objects/leads/${id}`
        : `/api/map-objects/customers/${id}`;
      // Resolve the real object on every replay. Saved action coordinates may
      // be stale and geocoder coordinates never identify a business object.
      await this.mapNavigation.locate(firstValueFrom(this.http.get<{
        featureKey: string; objectId: number; placeId: number;
        name: string | null; address: string | null; latitude: number; longitude: number;
      }>(objectUrl)).then(target => ({
        reference: { featureKey: target.featureKey, objectId: target.objectId },
        geometry: { type: 'Point', coordinates: [target.longitude, target.latitude] } as Point,
        popup: { type: target.featureKey === 'leads' ? 'Lead' : 'Customer',
          title: target.name || target.address || `${target.featureKey} ${target.objectId}`,
          subtitle: target.name ? target.address ?? undefined : undefined },
        pointIcon: focusPinIcon(), popupOffset: [0, -PIN_HEIGHT] as [number, number],
      })));
    } else if (!location.object) {
      // External addresses are coordinate navigation, not fabricated map objects.
      this.mapNavigation.navigateTo(location);
    } else {
      throw new Error('Unsupported map object.');
    }
  }

  constructor(
    private auth: AuthService,
    private zone: NgZone,
    private http: HttpClient,
    private agentContext: AppContextService,
    private surfaceActions: CurrentSurfaceActionService,
    private cache: CacheService,
    private mapNavigation: MapNavigationService,
    private zipCodeAreas: ZipCodeAreaFeature,
    public readonly voice: ConversationVoiceService,
    apiKeys: ApiKeysService,
  ) {
    this.auth.tenant?.sessionChanged$.subscribe(() => { void this.voice.stop(); });
    this.keyChanges = apiKeys.keysChanged$.subscribe(() => this.loadChatConfig(true));
  }

  ngOnDestroy(): void { this.keyChanges.unsubscribe(); }

  /** The chat key currently in effect: the temporary pick, else the company default
   *  (primary). Drives which picker row is highlighted. */
  get activeChatKeyId(): number | null { return this.chatKeyId(this.activeConversation); }

  private chatKeyId(conversation: CommandConversation): number | null {
    return conversation.selectedChatKeyId ?? this.chatKeys.find(k => k.isPrimary)?.id ?? null;
  }

  /** Select the CLI connection for this conversation. */
  selectAgent(): void { this.provider = 'codex'; }

  /** Select Agent API access with a specific key — a temporary override for the next
   *  turns; the company default (set in Services) is untouched. */
  selectChatKey(id: number | null): void { this.provider = 'chat'; this.selectedChatKeyId = id; }

  /** Fetch Agent API config (the configured keys) to drive the picker. Cached
   *  after the first load; pass force=true (on picker open) to pick up key changes.
   *  Silent on failure — the picker just shows no chat keys. */
  loadChatConfig(force = false): void {
    this.ensureAccount();
    if (force) this.cache.clear('chat');
    if (this.cache.has('chat', 'config')) return;
    const token = this.accountToken;
    // Explicit selections survive refresh/deletion; missing keys fail at execution.
    this.cache.load('chat', 'config', () =>
      this.http.get<{ keys: ChatKeyOption[] }>('/api/agent/config'),
    ).subscribe({
      error: () => {
        if (token === this.auth.getToken()) this.cache.clear('chat');
      }, // allow a retry without invalidating a different account's configuration
    });
  }

  open(): void { this.panelOpen = true; }
  close(): void { this.panelOpen = false; }

  async clear(): Promise<void> {
    const conversation = this.activeConversation;
    const token = this.accountToken;
    if (conversation.clearing || conversation.archiving) return;
    conversation.clearing = true;
    await this.voice.stop();
    conversation.loadGeneration = (conversation.loadGeneration ?? 0) + 1;
    this.messageLoads.delete(conversation.id);
    const poll = this.polls.get(conversation.id);
    if (poll) clearTimeout(poll);
    this.turns.get(conversation.id)?.abort.abort();
    this.turns.delete(conversation.id);
    const timer = this.saveTimers.get(conversation.id);
    if (timer) clearTimeout(timer);
    try {
      await this.saves.get(conversation.id)?.catch(() => {});
      if (token !== this.auth.getToken()) return;
      if (conversation.persisted) {
        await firstValueFrom(this.http.delete('/api/conversations/' + conversation.id + '/messages'));
        const fresh = await firstValueFrom(this.http.get<ConversationMetadata>('/api/conversations/' + conversation.id));
        if (token !== this.auth.getToken()) return;
        conversation.revision = fresh.revision;
      }
      conversation.messages = []; conversation.draft = ''; conversation.title = 'New conversation';
      conversation.beforeSequence = null;
      conversation.savedSettings = this.settings(conversation);
      conversation.unconfirmedTurn = undefined;
      conversation.saveError = conversation.voiceSaveError = conversation.loadError = conversation.sendError = undefined;
    } catch { if (token === this.auth.getToken()) conversation.loadError = 'Could not confirm clearing this conversation. Retry to reload saved messages.'; }
    finally { conversation.clearing = false; }
  }

  /** Voice talks only to BRee, the orchestrator. */
  async startVoice(): Promise<void> {
    const conversation = this.activeConversation;
    const token = this.auth.getToken();
    if (!token || this.voiceStarting || this.voice.state === 'connecting' || this.voice.state === 'live') return;
    if (conversation.clearing || conversation.archiving || conversation.unconfirmedTurn) {
      this.voice.error = 'Resolve the pending conversation change before starting voice.'; return;
    }
    this.voiceStarting = true;
    // Any stop while settings save (engine, provider, account, clear) cancels this start.
    const version = this.voice.version;
    try {
      await this.saveSettings(conversation);
      if (token !== this.auth.getToken() || conversation !== this.active || version !== this.voice.version) return;
      // Cold start: a resumed delegation ("do that") must carry the stored discussion, not only this
      // call's live transcript. Load existing history before the session opens, and abort safely if
      // the load fails or the account/conversation/generation changes while it runs.
      if (conversation.persisted && !conversation.loaded) {
        await this.loadMessages(conversation);
        if (token !== this.auth.getToken() || conversation !== this.active || version !== this.voice.version) return;
        if (!conversation.loaded) { this.voice.error = 'Could not load this conversation before starting voice.'; return; }
      }
      conversation.panelOpen = true;
      // Voice only ever reaches BRee; the provider (engine) must stay put for the call's lifetime.
      // The chat key is NOT captured here: a mid-call key switch must take effect at the next
      // dispatch, not reject every later delegation. Settings are resolved from the conversation
      // when each queued task is dispatched.
      const provider = conversation.provider;
      const current = () => token === this.auth.getToken() && this.items.includes(conversation) && !conversation.clearing && !conversation.isArchived;
      const fragments: { role: string; text: string; offset: number; messageId: string; consumed?: boolean }[] = [];
      const delegated = new Set<string>();
      let last: ChatMessage | undefined, lastEnd = 0, queue = Promise.resolve();
      const pending = new Set<string>(); let checkpoint: ReturnType<typeof setTimeout> | undefined;
      const flush = () => {
        if (checkpoint) clearTimeout(checkpoint); checkpoint = undefined;
        const ids = [...pending]; pending.clear();
        return Promise.all(ids.map(id => current() ? this.saveVoiceTranscript(conversation, id) : undefined));
      };
      this.voiceStarting = false;
      await this.voice.start('/api/conversations/' + conversation.id + '/voice', {}, token, event => {
        if (!current()) return;
        if (event.type === 'closed') { void flush(); return; }
        if (event.type === 'transcript' && event.text && event.role) {
          const role = event.role === 'user' ? 'user' : 'agent';
          if (!last || last.role !== role || (event.offsetMs ?? lastEnd) - lastEnd > 1200 || last.text.length + event.text.length > 16000) {
            last = { id: crypto.randomUUID(), voice: true, role, text: '', senderName: role === 'user' ? 'You' : 'BRee' };
            conversation.messages.push(last);
          }
          fragments.push({ role: event.role, text: event.text, offset: event.offsetMs ?? lastEnd, messageId: last.id! });
          if (fragments.length > 2000) fragments.splice(0, fragments.length - 2000);
          last.text += event.text; lastEnd = event.endMs ?? lastEnd;
          // Reloads can replace object instances while the live transcript is still growing.
          const displayed = conversation.messages.find(m => m.id === last!.id);
          if (displayed) displayed.text = last.text; else conversation.messages.push(last);
          // Fix the expected revision when text arrives; only known local writes may advance it.
          (displayed ?? last).voicePending ??= { revision: conversation.revision ?? 0 };
          pending.add(last.id!);
          checkpoint ??= setTimeout(() => { void flush(); }, 1000);
          return;
        }
        if (event.type !== 'delegation' || !event.delegationId || delegated.has(event.delegationId)) return;
        delegated.add(event.delegationId);
        const delegationId = event.delegationId, callId = this.voice.callId;
        const through = event.offsetMs;
        const fresh = fragments.filter(f => f.role === 'user' && !f.consumed && (through === undefined || f.offset <= through));
        if (!fresh.length) {
          this.voice.append(callId, 'No new transcribed request is available. Ask the user to clarify; no task was submitted.', delegationId);
          return;
        }
        // Consume actual fragments: timestamps can be shared and delegation offsets may be absent.
        for (const fragment of fresh) fragment.consumed = true;
        // Include both speakers so short confirmations and corrections retain their context.
        const context = fragments.filter(f => through === undefined || f.offset <= through).map(f => f.role + ': ' + f.text).join('\n').slice(-12000);
        const prompt = fresh.map(f => f.text).join('').trim().slice(-16000);
        // Carry spoken context the runtime has not seen: persisted voice history from before this
        // call (not among the live fragments) plus this call's running transcript.
        const priorVoice = this.voiceContext(conversation, new Set(fragments.map(f => f.messageId)));
        const clientContext = this.agentContext.build() + priorVoice + '\n\n## Current voice conversation (transcript data, may contain recognition errors)\n' + context;
        this.voice.append(callId, 'The request is queued for BRee. No action has completed yet.', delegationId, false);
        queue = queue.then(async () => {
          await this.turns.get(conversation.id)?.completion;
          if (!current() || conversation.provider !== provider || conversation.unconfirmedTurn) {
            this.voice.append(callId, 'The conversation changed. The queued request was not submitted.', delegationId); return;
          }
          if (this.isWorking(conversation)) {
            this.voice.append(callId, 'The conversation is busy. The request was not submitted; ask the user to retry when it is ready.', delegationId); return;
          }
          conversation.saving = true;
          let completion: Promise<ChatMessage>;
          try {
            await flush();
            await this.saveSettings(conversation);
            if (!current()) return;
            const turnId = crypto.randomUUID();
            // Resolve at dispatch from this call's conversation only; nothing captured at call start survives a settings change.
            completion = this.stream('/api/conversations/' + conversation.id + '/turns/stream', {
              turnId, prompt, clientContext, voiceSourceIds: [...new Set(fresh.map(f => f.messageId))].slice(-128),
              ...this.execution(conversation),
            }, conversation);
          } finally { conversation.saving = false; }
          // Like typed turns, only dispatch holds the conversation; the queue still serializes its tasks.
          const reply = await completion;
          this.voice.append(callId, reply.error ? 'The task failed or its outcome is unconfirmed. Check the conversation. ' + reply.text
            : reply.text || 'The agent returned no spoken summary. Check the conversation for its result.', delegationId);
        }).catch(() => {
          if (current()) conversation.sendError = 'Could not submit the voice request. Check the conversation before retrying.';
          this.voice.append(callId, 'Could not confirm task submission. Check the conversation before retrying.', delegationId);
        });
      });
    } catch { if (token === this.auth.getToken()) this.voice.error = 'Could not save this conversation before starting voice.'; }
    finally { this.voiceStarting = false; }
  }

  /** Bounded spoken transcript for the agent turn. Voice is a client-side GPT-Live channel the
   *  Codex runtime never processed, so a resumed session would otherwise lose it (FormatPrompt
   *  skips replayed history on resume). Drawn from the conversation's own messages, so it covers
   *  persisted history after a reload and still-pending checkpoints; it is untrusted recognition
   *  data and stays bounded. `exclude` drops messages already carried by another context section. */
  private voiceContext(conversation: CommandConversation, exclude?: Set<string>): string {
    const lines = conversation.messages
      .filter(m => m.voice && m.text.trim() && !(m.id && exclude?.has(m.id)))
      .map(m => (m.role === 'user' ? 'user' : 'assistant') + ': ' + m.text)
      .join('\n');
    return lines ? '\n\n## Voice conversation (transcript data, may contain recognition errors)\n' + lines.slice(-12000) : '';
  }

  /** Transcript writes share the settings chain, so local saves cannot race their expected revision. */
  private saveVoiceTranscript(conversation: CommandConversation, id: string): Promise<void> {
    const token = this.accountToken;
    return this.enqueueSave(conversation, async () => {
      const message = conversation.messages.find(m => m.id === id);
      const pending = message?.voicePending, text = message?.text;
      if (!message || !pending || token !== this.auth.getToken() || conversation.clearing || conversation.isArchived) return;
      try {
        await firstValueFrom(this.http.put('/api/conversations/' + conversation.id + '/voice/transcript', {
          id, ...pending, role: message.role === 'user' ? 'user' : 'assistant', text,
        }));
        if (token !== this.auth.getToken()) return;
        // A reload may have replaced the object; it shares the same pending record.
        const displayed = conversation.messages.find(m => m.id === id);
        let cleared = false;
        if (displayed?.voicePending === pending && displayed.text === text) { displayed.voicePending = undefined; cleared = true; }
        if (message.voicePending === pending && message.text === text) { message.voicePending = undefined; cleared = true; }
        if (!conversation.messages.some(m => m.voicePending)) conversation.voiceSaveError = undefined;
        // A messages GET that started before this durable save will not include this transcript (or
        // carries an older prefix). It would otherwise drop or shrink the now-saved checkpoint, since
        // the fresh-merge only retains still-pending voice messages and voicePending was just cleared.
        // Invalidate that in-flight load the same way clear()/send() do, then refresh to merge the
        // backend copy in its canonical order.
        if (cleared && this.messageLoads.has(conversation.id)) {
          conversation.loadGeneration = (conversation.loadGeneration ?? 0) + 1;
          this.messageLoads.delete(conversation.id);
          void this.loadMessages(conversation);
        }
      } catch (error) {
        if (token === this.auth.getToken()) conversation.voiceSaveError = (error as { status?: number })?.status === 409
          ? 'This conversation changed in another window. Some voice transcript was not saved.'
          : 'Some voice transcript could not be saved. Keep this conversation open and retry.';
      }
    });
  }

  async send(text: string, options: ChatExecutionOptions = {}): Promise<void> {
    const clean = text.trim();
    const conversation = this.activeConversation;
    const token = this.accountToken;
    // Same chain as delegated voice; composer options only fill fields saved settings and the key leave open.
    const execution = this.execution(conversation, options);
    if (clean.length > 16000) { conversation.sendError = 'Messages can contain up to 16,000 characters.'; return; }
    if (this.voice.state === 'connecting' || this.voiceStarting) { conversation.sendError = 'Wait for voice to connect, or end the call before sending.'; return; }
    if (!clean || this.isWorking(conversation) || conversation.unconfirmedTurn) return;
    conversation.saving = true;
    conversation.panelOpen = true;
    const voiceCall = this.voice.callId;
    try {
      if (conversation.persisted && !conversation.loaded) await this.loadMessages(conversation);
      if (conversation.persisted && !conversation.loaded) throw new Error('History could not be loaded.');
      if (!conversation.messages.length && conversation.title === 'New conversation')
        conversation.title = clean.replace(/\s+/g, ' ').slice(0, 100);
      conversation.draft = '';
      await this.saveSettings(conversation);
      if (token !== this.auth.getToken() || !this.items.includes(conversation)) return;
      conversation.loadGeneration = (conversation.loadGeneration ?? 0) + 1;
      const turnId = crypto.randomUUID();
      conversation.sendError = undefined;
      // Build spoken context now that history is loaded. Typed turns carry the same voice discussion
      // delegated turns do, so a resumed Codex session keeps it (e.g. a typed "do that"); building it
      // before the load would capture only the in-memory 'UI context' and lose the stored speech.
      const clientContext = this.agentContext.build() + this.voiceContext(conversation);
      conversation.messages.push({ role: 'user', text: clean, turnId });
      this.voice.append(voiceCall, 'The user typed a request, submitted to the backend: ' + clean, null, false);
      void this.stream('/api/conversations/' + conversation.id + '/turns/stream', {
        turnId, prompt: clean,
        clientContext, ...execution,
      }, conversation).then(reply => { this.voice.append(voiceCall, reply.error ? 'The task failed or its outcome is unconfirmed. ' + reply.text : reply.text); });
    } catch {
      if (token === this.auth.getToken()) {
        if (!conversation.draft) conversation.draft = clean;
        conversation.saveError ||= 'Could not save your conversation. Your message has not been sent.';
      }
    } finally { conversation.saving = false; }
  }

  canApplyLeadTableView(message: ChatMessage): boolean {
    return !!message.leadTableView
      && message.leadTableView.status !== 'applied'
      && this.surfaceActions.canApply(message.leadTableView.action);
  }

  applyLeadTableView(message: ChatMessage): void {
    const held = message.leadTableView;
    if (!held || held.status === 'applied') return;
    const receipt = this.surfaceActions.apply(held.action);
    if (!receipt) {
      held.feedback = 'Open Leads List or Board to apply this view.';
      return;
    }
    held.status = 'applied';
    held.receipt = receipt;
    held.feedback = receipt.note;
  }

  canRestoreLeadTableView(message: ChatMessage): boolean {
    const held = message.leadTableView;
    return !!held?.receipt
      && held.status === 'applied'
      && this.surfaceActions.canRestore(held.receipt);
  }

  restoreLeadTableView(message: ChatMessage): void {
    const held = message.leadTableView;
    if (!held?.receipt || held.status !== 'applied') return;
    if (!this.surfaceActions.restore(held.receipt)) {
      held.feedback = 'The page that applied this view is no longer open.';
      return;
    }
    held.status = 'restored';
    held.feedback = 'Previous view restored.';
  }

  canApplyLeadEmailDraft(message: ChatMessage): boolean {
    return !!message.leadEmailDraft
      && message.leadEmailDraft.status === 'proposed'
      && this.surfaceActions.canApplyEmailDraft(message.leadEmailDraft.action);
  }

  applyLeadEmailDraft(message: ChatMessage): void {
    const held = message.leadEmailDraft;
    if (!held || held.status !== 'proposed') return;
    if (!this.surfaceActions.applyEmailDraft(held.action)) {
      held.feedback = 'Open that lead to apply this email draft.';
      return;
    }
    held.status = 'applied';
    held.feedback = 'Draft filled locally. Review it, then use Send when ready.';
  }

  /** Shared streaming plumbing for both modes: POST the body, read the SSE
   *  response into a fresh in-progress reply bubble. fetch (not EventSource)
   *  because we must POST a body and attach the JWT bearer manually —
   *  EventSource does neither. */
  private stream(url: string, body: { turnId: string; prompt: string; clientContext: string | null; voiceSourceIds?: string[] } & ChatExecutionOptions, conversation: CommandConversation): Promise<ChatMessage> {
    const reply: ChatMessage = {
      role: 'agent', text: '', working: true, turnId: body.turnId,
      senderName: 'BRee',
    };
    conversation.messages.push(reply);
    this.turns.get(conversation.id)?.abort.abort();
    const abort = new AbortController();
    const token = this.auth.getToken();
    const turn: ConversationTurn = { conversation, turnId: body.turnId, prompt: body.prompt, abort, token };
    this.turns.set(conversation.id, turn);
    const completion = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal: abort.signal,
    }).then(async resp => {
      if (!this.isCurrentTurn(turn)) { void resp.body?.cancel(); return; }
      const diagnosticId = resp.headers?.get('X-Diagnostic-Id');
      if (diagnosticId && /^[a-f0-9]{32}$/.test(diagnosticId)) reply.diagnosticId = diagnosticId;
      if (!resp.ok || !resp.body) {
        if (resp.status !== 401 && resp.status !== 403) {
          const payload = await resp.json().catch(() => null);
          if (!this.isCurrentTurn(turn)) return;
          reply.problem = parseAgentProblem(payload?.problem);
          if (reply.problem) this.zone.run(() => {
            reply.text += (reply.text ? '\n\n' : '') + reply.problem!.message;
          });
        }
        this.finishWithError(reply, turn, resp.status);
        return;
      }
      return this.pump(resp.body.getReader(), reply, turn);
    }).catch(() => {
      // A superseded turn (aborted because a new turn started) just stops; a
      // genuine network failure shows an error.
      if (this.isCurrentTurn(turn)) this.finishWithError(reply, turn);
    }).finally(() => {
      this.zone.run(() => {
        reply.working = false;
        // All completion paths clean empty placeholders in their owning conversation.
        // A trace or typed action is useful content even without a prose reply.
        if (!reply.text && !reply.steps?.length && !reply.mapLocate
            && !reply.leadTableView && !reply.leadEmailDraft && !reply.overviewAnalyses?.length) {
          const index = conversation.messages.indexOf(reply);
          if (index !== -1) conversation.messages.splice(index, 1);
        }
      });
      if (this.turns.get(conversation.id) === turn) {
        this.turns.delete(conversation.id);
        if (token === this.auth.getToken()) void this.loadMessages(conversation);
      }
    }).then(() => reply);
    turn.completion = completion;
    return completion;
  }

  /** Read the SSE body, splitting on blank-line frame boundaries and applying
   *  each frame to the live reply. Mutations run inside the Angular zone so the
   *  transcript re-renders as tokens arrive. */
  private async pump(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    reply: ChatMessage,
    turn: ConversationTurn,
  ): Promise<void> {
    let done = false;
    for await (const frame of sseFrames(reader)) {
      if (!this.isCurrentTurn(turn)) break;
      if (this.applyFrame(frame, reply)) { done = true; break; }
    }
    if (!done && this.isCurrentTurn(turn)) throw new Error('Stream ended before completion.');
    this.zone.run(() => { reply.working = false; });
  }

  /** Apply one SSE frame: {"delta":"…"} appends reply text, {"step":"…"} grows
   *  the live process trace, a map-locate frame attaches a trusted location action,
   *  {"error":"…"} surfaces a message, and [DONE] ends the turn.
   *  Returns true when the turn is terminal so the reader stops (nothing after
   *  [DONE] is consumed). */
  private applyFrame(frame: string, reply: ChatMessage): boolean {
    const line = frame.split('\n').find(l => l.startsWith('data:'));
    if (!line) return false;
    const data = line.slice(5).trim();
    if (!data) return false;
    if (data === '[DONE]') { this.zone.run(() => { reply.working = false; }); return true; }
    try {
      const evt = JSON.parse(data) as {
        delta?: string;
        error?: string;
        problem?: unknown;
        step?: string;
        type?: string;
        label?: string;
        latitude?: number;
        longitude?: number;
        zoom?: number;
        object?: { featureKey?: unknown; objectId?: unknown };
        leadId?: number;
        subject?: string;
        body?: string;
        id?: unknown;
        reportId?: unknown;
      };
      this.zone.run(() => {
        if (typeof evt.step === 'string') (reply.steps ??= []).push(evt.step);
        if ((evt.type === 'map-locate' || evt.type === 'map-navigate') &&
            typeof evt.label === 'string' && evt.label.trim() &&
            typeof evt.latitude === 'number' && Number.isFinite(evt.latitude) &&
            evt.latitude >= -90 && evt.latitude <= 90 &&
            typeof evt.longitude === 'number' && Number.isFinite(evt.longitude) &&
            evt.longitude >= -180 && evt.longitude <= 180 &&
            (evt.zoom === undefined || (typeof evt.zoom === 'number' && Number.isInteger(evt.zoom) && evt.zoom >= 0 && evt.zoom <= 22))) {
          reply.mapLocate = {
            type: 'map-locate',
            label: evt.label.trim(),
            latitude: evt.latitude,
            longitude: evt.longitude,
            ...(evt.zoom === undefined ? {} : { zoom: evt.zoom }),
            ...(evt.object && typeof evt.object.featureKey === 'string' && evt.object.featureKey.trim()
              && ((typeof evt.object.objectId === 'string' && evt.object.objectId.length > 0)
                || (typeof evt.object.objectId === 'number' && Number.isSafeInteger(evt.object.objectId)))
              ? { object: { featureKey: evt.object.featureKey, objectId: evt.object.objectId } } : {}),
          };
          // Only trusted object-navigation tools emit this frame. Address
          // lookup keeps its existing click-to-locate action.
          if (evt.type === 'map-navigate') void this.locateOnMap(reply.mapLocate).catch(() => {
            reply.text += '\n\nCould not load this map object. Please try Locate on map again.';
          });
        }
        // Both transports receive typed actions from the same trusted backend tool boundary.
        if (evt.type === 'lead-table-view') {
          const action = parseLeadTableViewAction(evt);
          if (action) reply.leadTableView = { action, status: 'proposed' };
        }
        if (evt.type === 'lead-email-draft') {
          const action = parseLeadEmailDraftAction(evt);
          if (action) reply.leadEmailDraft = { action, status: 'proposed' };
        }
        const published = evt.type === 'overview-analysis-published' ? parseOverviewAnalysisPublished(evt) : null;
        if (published) {
          reply.overviewAnalyses ??= [];
          if (!reply.overviewAnalyses.some(p => p.id === published.id)) reply.overviewAnalyses.push(published);
          this.overviewAnalysisSource.next(published);
        }
        if (typeof evt.delta === 'string') {
          reply.text += evt.delta;
        } else if (typeof evt.error === 'string') {
          reply.problem = parseAgentProblem(evt.problem);
          reply.text += (reply.text ? '\n\n' : '') + evt.error;
          reply.working = false;
          reply.error = true;
        }
      });
    } catch { /* keepalive / partial frame — ignore */ }
    return false;
  }

  private isCurrentTurn(turn: ConversationTurn): boolean {
    return !turn.abort.signal.aborted && turn.token === this.auth.getToken()
      && this.turns.get(turn.conversation.id) === turn;
  }

  private finishWithError(
    reply: ChatMessage,
    turn: ConversationTurn,
    status?: number,
  ): void {
    turn.conversation.unconfirmedTurn = { turnId: turn.turnId, prompt: turn.prompt };
    turn.conversation.sendError = status === 409
      ? 'This conversation changed in another window. Checking whether your message was saved.'
      : 'Could not confirm your message. Retry to check its saved status before sending again.';
    // A 401 means the JWT expired/was revoked. fetch bypasses the app's
    // UnauthorizedInterceptor, so trigger the shared session-expired flow here
    // (re-login dialog) the same way an HttpClient 401 would.
    if (status === 401) this.auth.handleSessionExpired();
    this.zone.run(() => {
      reply.working = false;
      reply.error = true;
      if (!reply.text) {
        reply.text = status === 409
          ? 'This conversation changed in another window. Reload it before sending again.'
          : status === 401
          ? 'Your session expired — please sign in again.'
          : status === 403
            ? 'You don’t have access to chat here.'
            : 'The assistant is unavailable right now.';
      }
    });
  }

  async loadHistory(more = false, archived = false): Promise<void> {
    this.ensureAccount();
    const history = archived ? this.archiveHistory : this.history;
    const token = this.accountToken;
    if (!token || history.loading || (!more && history.loaded)) return;
    history.loading = true;
    const offset = more ? history.nextOffset : 0;
    if (offset === null) { history.loading = false; return; }
    const generation = history.generation;
    const initial = this.active;
    const selection = this.selectionRevision;
    try {
      const page = await firstValueFrom(this.http.get<{ items: ConversationMetadata[]; nextOffset: number | null }>(
        '/api/conversations', { params: archived ? { offset, archived: true } : { offset } }));
      if (token !== this.auth.getToken() || generation !== history.generation) return;
      for (const item of page.items) {
        const cached = this.items.find(c => c.id === item.id);
        if (cached) {
          if (!cached.archiving && cached.pendingArchive === undefined && item.revision >= (cached.revision ?? 0)) cached.isArchived = item.isArchived;
          continue;
        }
        const conversation: CommandConversation = { ...item, messages: [], panelOpen: false, persisted: true, loaded: false };
        conversation.savedSettings = this.settings(conversation);
        this.items.push(conversation);
      }
      history.nextOffset = page.nextOffset;
      if (!archived && this.initialHistorySelection && selection === this.selectionRevision && this.active === initial && !initial.persisted
          && !initial.messages.length && !initial.draft && initial.title === 'New conversation' && !initial.saving
          && page.items.length) {
        this.items = this.items.filter(c => c !== initial);
        this.selectConversation(page.items[0].id, { openPanel: initial.panelOpen });
      }
      this.refreshVisibleConversations();
      if (!archived) this.initialHistorySelection = false;
      history.loaded = true;
      history.error = '';
    } catch { if (token === this.auth.getToken() && generation === history.generation) history.error = 'Could not load conversations. Please retry.'; }
    finally { if (token === this.auth.getToken() && generation === history.generation) history.loading = false; }
  }

  async retryPersistence(): Promise<void> {
    this.ensureAccount();
    if (this.retrying) return;
    const token = this.accountToken;
    // Retry the failed owners, not whichever conversation happens to be selected.
    const failed = this.items.filter(c => this.conversationError(c));
    this.retrying = true;
    try {
      if (this.history.error || !this.history.loaded) {
        this.history.loaded = false;
        await this.loadHistory();
      }
      if (token !== this.auth.getToken()) return;
      if (this.archiveHistory.error) {
        this.archiveHistory.loaded = false;
        await this.loadHistory(false, true);
      }
      for (const conversation of failed) {
        if (token !== this.auth.getToken()) return;
        if (conversation.unconfirmedTurn || conversation.loadError) await this.loadMessages(conversation);
        if (token !== this.auth.getToken()) return;
        try {
          if (conversation.persisted && (conversation.saveError || conversation.voiceSaveError || conversation.pendingArchive !== undefined)) {
            const latest = await firstValueFrom(this.http.get<ConversationMetadata>('/api/conversations/' + conversation.id));
            if (token !== this.auth.getToken()) return;
            conversation.revision = latest.revision;
          }
          if (conversation.pendingArchive !== undefined) {
            await this.setArchived(conversation.id, conversation.pendingArchive);
            continue;
          }
          await this.saveSettings(conversation);
          // Keep each transcript's original revision: an unknown external change must reject it.
          for (const message of conversation.messages.filter(m => m.voicePending))
            await this.saveVoiceTranscript(conversation, message.id!);
          if (token === this.auth.getToken() && !conversation.unconfirmedTurn) conversation.sendError = undefined;
        } catch {
          if (token === this.auth.getToken()) conversation.saveError ||= 'Could not save this conversation. Please retry.';
        }
      }
    } finally { this.retrying = false; }
  }

  private settings(c: CommandConversation): string {
    return JSON.stringify({ title: c.title, draft: c.draft, provider: c.provider, selectedChatKeyId: c.selectedChatKeyId, sessions: c.sessions ?? [] });
  }

  private scheduleSave(conversation: CommandConversation): void {
    const timer = this.saveTimers.get(conversation.id);
    if (timer) clearTimeout(timer);
    if (!this.accountToken || conversation.clearing || conversation.archiving) return;
    this.saveTimers.set(conversation.id, setTimeout(() => {
      this.saveTimers.delete(conversation.id);
      void this.saveSettings(conversation).catch(() => { /* visible retry state */ });
    }, 500));
  }

  /** Runs one write after the conversation's previous write. Queued work must not await this chain. */
  private enqueueSave(conversation: CommandConversation, write: () => Promise<void>): Promise<void> {
    const save = (this.saves.get(conversation.id) ?? Promise.resolve()).catch(() => {}).then(write);
    this.saves.set(conversation.id, save);
    return save;
  }

  private saveSettings(conversation: CommandConversation): Promise<void> {
    const token = this.accountToken;
    return this.enqueueSave(conversation, async () => {
      if (!token || token !== this.auth.getToken() || !this.items.includes(conversation)) throw new Error('Account changed.');
      if (!conversation.persisted) {
        const created = await firstValueFrom(this.http.post<ConversationMetadata>('/api/conversations', { id: conversation.id }));
        if (token !== this.auth.getToken()) throw new Error('Account changed.');
        conversation.persisted = true; conversation.revision = created.revision;
      }
      const settings = this.settings(conversation);
      if (settings === conversation.savedSettings && !conversation.saveError) return;
      const expected = conversation.revision;
      const updated = await firstValueFrom(this.http.put<ConversationMetadata>('/api/conversations/' + conversation.id,
        { ...JSON.parse(settings), revision: expected }));
      if (token !== this.auth.getToken()) return;
      conversation.revision = updated.revision;
      // This known local write may carry transcripts expecting its revision; stale ones stay stale.
      for (const { voicePending } of conversation.messages)
        if (voicePending && voicePending.revision === expected) voicePending.revision = updated.revision;
      conversation.savedSettings = settings;
      conversation.saveError = undefined;
    }).catch((error: { status?: number }) => {
      if (token === this.auth.getToken()) conversation.saveError = error?.status === 409
        ? 'This conversation changed in another window. Retry to save your changes.'
        : 'Could not save conversation changes. Please retry.';
      throw error;
    });
  }

  loadOlderMessages(): void { void this.loadMessages(this.activeConversation, true); }

  private loadMessages(conversation: CommandConversation, older = false): Promise<void> {
    if (!conversation.persisted || conversation.clearing || this.turns.has(conversation.id)) return Promise.resolve();
    older = older && !conversation.unconfirmedTurn;
    const previous = this.messageLoads.get(conversation.id);
    if (previous) return previous;
    const token = this.accountToken;
    this.loadingMessages = true;
    const generation = conversation.loadGeneration;
    const load = (async () => {
      try {
        const page = await firstValueFrom(this.http.get<{ items: StoredMessage[]; beforeSequence: number | null }>(
          '/api/conversations/' + conversation.id + '/messages',
          { params: older && conversation.beforeSequence ? { before: conversation.beforeSequence } : {} }));
        if (token !== this.auth.getToken() || generation !== conversation.loadGeneration || this.turns.has(conversation.id)) return;
        const unconfirmed = conversation.unconfirmedTurn;
        let accepted = unconfirmed && page.items.some(m => m.turnId === unconfirmed.turnId && m.role === 'user');
        let before = page.beforeSequence;
        // A later visit can have more than one page since the uncertain request.
        while (unconfirmed && !accepted && before !== null) {
          const earlier = await firstValueFrom(this.http.get<{ items: StoredMessage[]; beforeSequence: number | null }>(
            '/api/conversations/' + conversation.id + '/messages', { params: { before } }));
          if (token !== this.auth.getToken() || generation !== conversation.loadGeneration) return;
          accepted = earlier.items.some(m => m.turnId === unconfirmed.turnId && m.role === 'user');
          before = earlier.beforeSequence;
        }
        // A streamed reply has no database ID yet. Match its turn and role before
        // retaining a local failure: the history response may already contain it.
        const isStored = (message: ChatMessage): boolean => page.items.some(item =>
          item.id === message.id || (!!message.turnId && item.turnId === message.turnId && item.role === message.role));
        const unsent = conversation.messages.filter(m => !m.id && !isStored(m) && (
          (m.role === 'user' && m.status === 'failed')
          || (m.role === 'agent' && m.error === true && !!m.problem)));
        if (unconfirmed) {
          if (accepted) {
            if (unconfirmed.restoredDraft && conversation.draft === unconfirmed.prompt) {
              conversation.draft = '';
              this.scheduleSave(conversation);
            }
            conversation.sendError = undefined;
          } else {
            if (!conversation.draft || conversation.draft === unconfirmed.prompt) {
              conversation.draft = unconfirmed.prompt;
              this.scheduleSave(conversation);
            } else {
              // Keep the rejected question visible without overwriting a newer draft.
              unsent.push({ role: 'user', text: unconfirmed.prompt, turnId: unconfirmed.turnId, status: 'failed' });
            }
            conversation.sendError = 'Your message was not sent. Your question has been kept; review it before sending again.';
          }
          conversation.unconfirmedTurn = undefined;
        }
        const messages = page.items.map(item => {
          const restored = this.restoreMessage(item);
          const previous = conversation.messages.find(m => !m.id && !!m.turnId
            && m.turnId === restored.turnId && m.role === restored.role)
            ?? conversation.messages.find(m => m.id === restored.id);
          if (restored.error && previous) {
            restored.problem = previous.problem;
            restored.diagnosticId = previous.diagnosticId;
          }
          if (previous?.voicePending) { restored.voicePending = previous.voicePending; restored.text = previous.text; }
          if (previous?.leadTableView) restored.leadTableView = previous.leadTableView;
          if (previous?.leadEmailDraft) restored.leadEmailDraft = previous.leadEmailDraft;
          return restored;
        });
        conversation.messages = older
          ? [...messages, ...conversation.messages.filter(m => !isStored(m))]
          // The backend owns saved voice history and its order. Retain only local checkpoints
          // that are still pending a durable write; a saved voice message absent from the latest
          // page (older than it, or cleared elsewhere) must not be resurrected out of order.
          : [...messages, ...conversation.messages.filter(m => m.voice && m.voicePending && !isStored(m)), ...unsent];
        conversation.loadError = undefined;
        conversation.beforeSequence = page.beforeSequence;
        conversation.loaded = true;
        const timer = this.polls.get(conversation.id);
        if (timer) clearTimeout(timer);
        if (messages.some(m => m.working)) this.polls.set(conversation.id,
          setTimeout(() => { if (token === this.auth.getToken()) void this.loadMessages(conversation); }, 3000));
      } catch {
        if (token === this.auth.getToken() && generation === conversation.loadGeneration) {
          conversation.loadError = 'Could not load messages. Please retry.';
          const unconfirmed = conversation.unconfirmedTurn;
          if (unconfirmed && !conversation.draft) {
            conversation.draft = unconfirmed.prompt;
            unconfirmed.restoredDraft = true;
            this.scheduleSave(conversation);
          }
        }
      }
      finally {
        if (token === this.auth.getToken() && generation === conversation.loadGeneration) {
          this.loadingMessages = false; this.messageLoads.delete(conversation.id);
        }
      }
    })();
    this.messageLoads.set(conversation.id, load);
    return load;
  }

  private restoreMessage(item: StoredMessage): ChatMessage {
    const message: ChatMessage = { id: item.id, turnId: item.turnId, sequence: item.sequence, role: item.role,
      senderName: item.senderName, text: item.text, status: item.status, turn: item.turn,
      working: item.status === 'running' && Date.now() - Date.parse(item.updatedAt) < 120000,
      error: item.status === 'failed' };
    if (item.status === 'running' && !message.working) message.status = 'interrupted';
    try {
      const parts = JSON.parse(item.parts);
      message.voice = parts.voice === true;
      if (Array.isArray(parts.voiceSourceIds) && parts.voiceSourceIds.every((id: unknown) => typeof id === 'string')) message.voiceSourceIds = parts.voiceSourceIds;
      message.steps = parts.steps;
      if (parts.mapLocate) message.mapLocate = { type: 'map-locate', ...parts.mapLocate };
      const table = parseLeadTableViewAction(parts.leadTableView);
      const email = parseLeadEmailDraftAction(parts.leadEmailDraft);
      if (table) message.leadTableView = { action: table, status: 'proposed' };
      if (email) message.leadEmailDraft = { action: email, status: 'proposed' };
      const reports = Array.isArray(parts.overviewAnalyses)
        ? parts.overviewAnalyses.map(parseOverviewAnalysisPublished).filter((p: OverviewAnalysisPublished | null) => !!p) : [];
      if (reports.length) message.overviewAnalyses = reports;
    } catch { /* The visible text still survives an unknown future parts version. */ }
    return message;
  }

}

interface ConversationMetadata {
  id: string; title: string; draft: string; provider: ProviderMode;
  selectedChatKeyId: number | null; revision: number; isArchived: boolean; sessions?: SessionSettings[];
}
interface StoredMessage {
  turn?: ChatMessage['turn'];
  id: string; turnId: string; sequence: number; role: 'user' | 'agent'; senderName: string;
  text: string; parts: string; status: 'running' | 'completed' | 'failed' | 'interrupted'; updatedAt: string;
}
